import {
  ClientData,
  LanguageNameSpaces,
  OpenMessage,
  OrderTypes,
} from "@shared/types";
import { useContext, useEffect, useState } from "react";
import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context";
import { TRADE_DEFAULT_STOP } from "@shared/features/tradingTerminal/common/constants";
import {
  CreateTradeSections,
  OnChangeWithWarning,
} from "@shared/features/tradingTerminal/common/types";
import { UseSymbolAndAccountSelectors } from "@shared/features/bots/BotForm/common/types";
import {
  getAmountValidationMessage,
  getValidationMessage,
} from "@shared/features/tradingTerminal/common/utils";
import { useConnectedAmountPercentInputsProps } from "@shared/common/Inputs/useConnectedAmountPercentInputs";
import { round } from "lodash";
import { ErrorTypes } from "@shared/features/trades/common/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface useSharedGeneralProps {
  openMessage: OpenMessage;
  useSymbolAndAccountSelectors?: UseSymbolAndAccountSelectors;
  onChangeWithWarning: OnChangeWithWarning;
}

const useSharedGeneral = ({
  openMessage,
  useSymbolAndAccountSelectors,
  onChangeWithWarning,
}: useSharedGeneralProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADING_TERMINAL);
  const {
    account,
    symbols,
    setSymbols,
    setAccount,
    amountCoin,
    quantityCoin,
    amount,
    setAmount,
    amountPercent,
    setAmountPercent,
    availableAmount,
    minAmount,
    quantity,
    setQuantity,
    quantityPercent,
    setQuantityPercent,
    availableQuantity,
    minQuantity,
    isLoading,
    isFetching,
    setGeneralDataProcessed,
    getCreateGeneralDataQuery,
    setFreeTextTradeData,
    setRawFreeText,
    freeTextTradeData,
    setUseFreeTextData,
    setFreeTextProcessed,
    freeTextCreateTradeLoading,
    generalErrors,
    symbolChanged,
    setSymbolChanged,
    setTabWithErrors,
    setAmountInitialized,
    precisionConfig,
    extraInfo,
    setShowedOppositePositionError,
    setValuesModified,
    amountInitialized,
    isSignal,
    setOrdersInitialized,
    setStopDistanceInputModified,
    useFreeTextData,
    accountChanged,
    setAccountChanged,
    setPriceTargetsModified,
    setDefaultStop,
    setExtraInfo,
    setPrevGeneralOptionsMapping,
    useTradingTerminalOldVersion,
    setStrategyValuesModified,
    setShowNewCustomStrategy,
    setUseTrailing,
    setUseStopOrders,
    setTrailingTakeProfit,
    setTrailingEntry,
    setTrailingStop,
    setNumOrdersChangedManually,
  } = useContext(TradingTerminalContext)!;

  const { isFetching: generalDataFetching, error: generalDataError } =
    getCreateGeneralDataQuery;

  const [showExistingAmount, setShowExistingAmount] = useState(false);
  const [advancedExpanded, setAdvancedExpanded] = useState(false);
  const [currentAccount, setCurrentAccount] = useState<ClientData>();

  const symbolAndAccountSelectorsData = useSymbolAndAccountSelectors?.({
    account,
    symbols,
    setSymbols,
    setAccount,
    initialize: !symbols.length,
    isTrade: true,
    onSymbolChanged: () => setSymbolChanged(true),
    onAccountChanged: () => {
      if (useFreeTextData) setAccountChanged(true);
    },
    useFreeText: useFreeTextData,
    defaultSymbols: freeTextTradeData ? [freeTextTradeData.symbol] : [],
    defaultExchange: freeTextTradeData ? currentAccount?.exchange : undefined,
    defaultAccountId: accountChanged ? account?.client_id : undefined,
    onChangeWithWarning,
  });

  const { symbolError, accountError } = symbolAndAccountSelectorsData || {};
  const hasError = !!symbolError || !!accountError;

  const amountInputSharedProps = {
    amountPrecision: precisionConfig?.amount,
    percentPrecision: precisionConfig?.percent,
    availableAmount: availableAmount,
    amount:
      amount !== undefined
        ? round(Number(amount || "0"), precisionConfig?.amount).toString()
        : undefined,
    onAmountChange: setAmount,
    percent: amountPercent !== undefined ? amountPercent || "0" : undefined,
    onPercentChange: setAmountPercent,
    useDebounce: true,
    onChange: () => setValuesModified(true),
  };

  const quantityInputSharedProps: useConnectedAmountPercentInputsProps = {
    amountPrecision: precisionConfig?.quantity,
    percentPrecision: precisionConfig?.percent,
    availableAmount: availableQuantity,
    amount: quantity || "0",
    onAmountChange: setQuantity,
    percent: quantityPercent || "0",
    onPercentChange: setQuantityPercent,
    useDebounce: true,
    onChange: () => setValuesModified(true),
  };

  const amountInputTitle = `${t("labels.new_amount")} (${amountCoin})`;
  const quantityInputTitle = t("labels.existing_amount_with_coin", {
    coin: quantityCoin,
  });
  const invalidTradeAmount = ErrorTypes.INVALID_TRADE_AMOUNT in generalErrors;
  const invalidTradeQuantity =
    ErrorTypes.INVALID_TRADE_QUANTITY in generalErrors;
  const maxNumTradesExceeded =
    ErrorTypes.MAX_NUM_TRADES_EXCEEDED in generalErrors;

  const amountValidationMessage = getAmountValidationMessage(
    t,
    minAmount,
    availableAmount,
    amountCoin,
    !!extraInfo?.leverage,
    useTradingTerminalOldVersion
  );
  const quantityValidationMessage = getValidationMessage(
    t,
    minQuantity,
    availableQuantity
  );

  const notEnoughExistingQuantity =
    Math.abs(minQuantity || 0) > Math.abs(availableQuantity || 0);
  let existingQuantityWarningMessage = "";
  if (notEnoughExistingQuantity) {
    existingQuantityWarningMessage = t(
      "messages.errors.not_enough_existing_quantity",
      {
        coin: quantityCoin || "existing quantity",
        min: minQuantity,
      }
    );
  }

  const onExistingQuantityClick = existingQuantityWarningMessage
    ? () => {
        if (existingQuantityWarningMessage) {
          openMessage(existingQuantityWarningMessage, "warning");
        }
      }
    : undefined;

  useEffect(() => {
    if (
      !isLoading &&
      !!symbolError &&
      (amountInitialized || generalDataError)
    ) {
      setAmount("0");
      setAmountPercent("0");
    }
  }, [isLoading, !!symbolError, amountInitialized, generalDataError]);

  useEffect(() => {
    if (!freeTextCreateTradeLoading && freeTextTradeData) {
      if (
        !!symbols[0] &&
        !!account &&
        freeTextTradeData.symbol !== symbols[0]
      ) {
        setSymbols([]);
        setCurrentAccount(account);
        setAccount(undefined);
        if (accountChanged) setAccountChanged(false);
      }
      if (accountChanged) {
        setCurrentAccount(account);
      }
      setUseFreeTextData(true);
      setFreeTextProcessed(false);
      setStopDistanceInputModified(false);
      resetOrders();
    }
  }, [freeTextCreateTradeLoading, freeTextTradeData, accountChanged]);

  useEffect(() => {
    if (generalDataFetching) {
      setGeneralDataProcessed(false);
      setShowedOppositePositionError(false);
      setValuesModified(false);
      setExtraInfo(undefined);
      setPrevGeneralOptionsMapping(undefined);
      resetOrders();
      Object.values(OrderTypes).map((orderType) => {
        setUseTrailing(orderType, false);
      });
      setTrailingTakeProfit(undefined);
      setTrailingEntry(undefined);
      setTrailingStop(undefined);
      if (symbolChanged) {
        setRawFreeText(undefined);
        setFreeTextTradeData(undefined);
        setUseFreeTextData(false);
        setFreeTextProcessed(false);
        setSymbolChanged(false);
        setAccountChanged(false);
      }
    }
  }, [generalDataFetching, symbolChanged, accountChanged, useFreeTextData]);

  useEffect(() => {
    if (!isLoading && amountInitialized) {
      setTabWithErrors(
        CreateTradeSections.GENERAL,
        Object.keys(generalErrors || {}).length > 0
      );
    }
  }, [isLoading, amountInitialized, Object.keys(generalErrors || {}).length]);

  const resetOrders = () => {
    if (!isSignal || !useFreeTextData) {
      setDefaultStop(TRADE_DEFAULT_STOP);
      setUseStopOrders(false);
    }
    if (!isSignal) {
      setAmountInitialized(false);
    }
    Object.values(OrderTypes).map((orderType) => {
      setPriceTargetsModified(orderType, false);
      setOrdersInitialized(orderType, false);
      setStrategyValuesModified(orderType, false);
      setShowNewCustomStrategy(orderType, false);
      setNumOrdersChangedManually(orderType, false);
    });
  };

  const onAddExistingAmountClick = () => {
    setShowExistingAmount(true);
  };

  const onRemoveExistingAmountClick = () => {
    setQuantity("0");
    setShowExistingAmount(false);
  };

  return {
    amountInputSharedProps,
    quantityInputSharedProps,
    onAddExistingAmountClick,
    onRemoveExistingAmountClick,
    amountInputTitle,
    quantityInputTitle,
    showExistingAmount,
    advancedExpanded,
    setAdvancedExpanded,
    generalErrors,
    isLoading,
    isFetching,
    amountValidationMessage,
    quantityValidationMessage,
    existingQuantityWarningMessage,
    amountCoin,
    quantityCoin,
    symbols,
    onExistingQuantityClick,
    invalidTradeAmount,
    invalidTradeQuantity,
    maxNumTradesExceeded,
    useTradingTerminalOldVersion,
    amountInitialized,
    symbolAndAccountSelectorsData,
    hasError,
  };
};

export default useSharedGeneral;
